import React from 'react';
import { X, MessageCircle, Calendar, BookOpen, Package, Users, Settings, MapPin, Image } from 'lucide-react';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage: 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload';
  onPageChange: (page: 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload') => void;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  currentPage,
  onPageChange,
}) => {
  const menuItems = [
    {
      id: 'chats' as const,
      label: 'Chats',
      icon: <MessageCircle className="h-5 w-5" />,
    },
    {
      id: 'slots' as const,
      label: 'Trip Slots',
      icon: <Calendar className="h-5 w-5" />,
    },
    {
      id: 'bookings' as const,
      label: 'Bookings',
      icon: <BookOpen className="h-5 w-5" />,
    },
    {
      id: 'inventory' as const,
      label: 'Inventory',
      icon: <Package className="h-5 w-5" />,
    },
    {
      id: 'assignment' as const,
      label: 'Assignment',
      icon: <Users className="h-5 w-5" />,
    },
    {
      id: 'admin-work' as const,
      label: 'Admin Work',
      icon: <Settings className="h-5 w-5" />,
    },
    {
      id: 'destinations' as const,
      label: 'Destinations',
      icon: <MapPin className="h-5 w-5" />,
    },
    {
      id: 'image-upload' as const,
      label: 'Image Upload',
      icon: <Image className="h-5 w-5" />,
    },
  ];

  const handlePageChange = (page: 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload') => {
    onPageChange(page);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Menu */}
      <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 lg:hidden transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Menu Items */}
        <div className="p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handlePageChange(item.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  currentPage === item.id
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <div className={`${
                  currentPage === item.id ? 'text-primary-600' : 'text-gray-500'
                }`}>
                  {item.icon}
                </div>
                <span className="font-medium">{item.label}</span>
                {currentPage === item.id && (
                  <div className="ml-auto w-2 h-2 bg-primary-600 rounded-full" />
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};
